"use client";

import { useState, useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

const faqs = [
  {
    q: "Is DogFob free to use?",
    a: "Yes — DogFob's core features including pet profiles, reminders, and basic sharing are completely free. A premium plan unlocks unlimited GPS device connections, extended location history, and priority support. Pricing starts at $4.99/month.",
  },
  {
    q: "Which GPS collars and trackers does DogFob support?",
    a: "DogFob integrates with Tractive, Whistle, Fi, Garmin, Jiobit, and most Bluetooth and GPS tracking tags. If your device isn't listed, you can still use manual check-ins. We're adding new device integrations every month.",
  },
  {
    q: "How does sharing work? Can I control what my vet sees?",
    a: "Absolutely. Every share is permission-based. When you add someone to your trusted circle, you choose exactly which sections they can see — location, health records, reminders, or any combination. You can revoke access at any time, instantly.",
  },
  {
    q: "Is my pet\'s health data private and secure?",
    a: "Your data is encrypted in transit and at rest. DogFob is SOC 2 compliant and never sells or shares your data with third parties. All sharing is strictly opt-in and controlled by you. We log every access so you always have a full audit trail.",
  },
  {
    q: "Can I add more than one pet?",
    a: "Yes! DogFob supports unlimited pets on all plans — dogfobs, cats, rabbits, birds, and more. Each pet gets their own complete profile, and you can switch between them with a single tap.",
  },
  {
    q: "What happens if my pet goes missing?",
    a: "If your pet has a GPS tracker connected, you'll see their live location on the map. You can also generate a shareable 'Lost Pet' card from their profile — it includes their photo, microchip ID, and your contact number. Neighbors and shelters can scan a QR code to report a sighting.",
  },
];

export default function OwnerFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".scroll-fade-up").forEach((el, i) => {
              setTimeout(() => el.classList.add("is-visible"), i * 80);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-white dark:bg-[#0f172a]">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-14 scroll-fade-up">
          <span className="teal-badge mb-4">FAQ</span>
          <h2 className="text-4xl font-extrabold text-[#1A2B2B] dark:text-slate-100 tracking-tight mt-4 mb-4">
            Questions? We've got answers.
          </h2>
          <p className="text-[#4A6363] dark:text-slate-400 text-lg">
            Everything you need to know about DogFob for pet owners.
          </p>
        </div>

        <div className="flex flex-col gap-0">
          {faqs?.map((faq, idx) => (
            <div key={`faq-${idx}`} className="scroll-fade-up faq-item">
              <button
                className="w-full flex items-center justify-between py-5 text-left gap-4 hover:text-teal-600 transition-colors duration-200"
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                aria-expanded={openIndex === idx}
              >
                <span className="text-base font-semibold text-[#1A2B2B] dark:text-slate-100 font-heading pr-2">
                  {faq?.q}
                </span>
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                    openIndex === idx ? "bg-teal-500 rotate-45" : "bg-[#F7F8F6] dark:bg-slate-700"
                  }`}
                >
                  <Icon
                    name="PlusIcon"
                    size={14}
                    className={openIndex === idx ? "text-white" : "text-[#4A6363]"}
                  />
                </div>
              </button>
              <div
                className="faq-answer"
                style={{ maxHeight: openIndex === idx ? "300px" : "0" }}
              >
                <p className="pb-5 text-[#4A6363] dark:text-slate-400 leading-relaxed text-sm">{faq?.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}